<script setup lang="ts">
import { ref, computed } from 'vue'
import { Check, Search, ChevronDown, Loader2 } from 'lucide-vue-next'
import type { ToolActivity } from '~/types/web-chat'

const props = defineProps<{
  activity: ToolActivity
}>()

const isExpanded = ref(false)
const detailItems = computed(() => props.activity.details?.length ? props.activity.details : props.activity.queries)
const hasDetails = computed(() => (detailItems.value?.length ?? 0) >= 1)

function toggle() {
  if (hasDetails.value) {
    isExpanded.value = !isExpanded.value
  }
}
</script>

<template>
  <div class="flex flex-col items-start gap-1 w-full">
    <button
      type="button"
      class="tool-chip inline-flex items-center gap-2 py-1 px-2.5 rounded-full text-[11px] border transition-all duration-300"
      :class="[
        activity.completed
          ? 'bg-muted/40 border-border text-muted-foreground'
          : 'bg-primary-lighter border-primary/20 text-foreground',
        hasDetails ? 'hover:bg-accent-lighter hover:border-border cursor-pointer' : 'cursor-default'
      ]"
      @click="toggle"
    >
      <component
        :is="activity.completed ? Check : Search"
        class="h-3 w-3 shrink-0"
        :class="activity.completed ? 'text-success' : 'text-primary'"
      />

      <span class="truncate">{{ activity.label.replace(/\.{3}$/, '') }}</span>
      <span v-if="(activity.count ?? 1) > 1" class="shrink-0 text-muted-foreground/70">×{{ activity.count }}</span>

      <Loader2 v-if="!activity.completed" class="h-3 w-3 shrink-0 animate-spin text-primary/60" />

      <ChevronDown
        v-if="hasDetails"
        class="h-3 w-3 shrink-0 transition-transform duration-200 text-muted-foreground/60"
        :class="{ 'rotate-180': isExpanded }"
      />
    </button>

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform scale-95 opacity-0 -translate-y-1"
      enter-to-class="transform scale-100 opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform scale-100 opacity-100 translate-y-0"
      leave-to-class="transform scale-95 opacity-0 -translate-y-1"
    >
      <div v-if="isExpanded && detailItems" class="ml-6 w-[min(32rem,calc(100vw-8rem))] rounded-md border border-border bg-muted/20 px-2.5 py-2 space-y-1.5">
        <div
          v-for="(query, idx) in detailItems"
          :key="idx"
          class="flex items-start gap-1.5"
        >
          <div class="mt-1.5 h-1 w-1 rounded-full bg-border shrink-0" />
          <span class="text-[10px] text-muted-foreground/85 leading-snug">
            {{ query }}
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.tool-chip {
  animation: chip-enter 0.2s ease-out;
}

@keyframes chip-enter {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
